import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Palette } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type { BrandKit } from '@/lib/api/types';
import { DEFAULT_BRAND_KIT } from '@/lib/branding/defaultBrandKit';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { IdentityTab } from '../brand-studio/tabs/IdentityTab';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';

/**
 * Brand Identity — the brand-level defaults for logo, colors, font and
 * campaign name. Everything experience/game/prize that used to live in
 * the old "Brand Studio" page now lives per-event in `BrandStudioEvent`
 * (managed from the Event Detail page).
 *
 * The form still posts to `/auth/brandkit` (which writes to `co_brandkits`)
 * — only the Identity fields are sent; other fields in the row are left
 * untouched. This keeps backwards-compat with existing data.
 */
export function BrandIdentityPage() {
  const queryClient = useQueryClient();
  const t = useT();
  useFavoritable(t('brandIdentity.title'));
  const [uploading, setUploading] = useState(false);
  const [kit, setKit] = useState<BrandKit>(DEFAULT_BRAND_KIT);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.auth.brandKit,
    queryFn: () => api.get<BrandKit | null>('/auth/brandkit'),
  });

  useEffect(() => {
    if (data) setKit({ ...DEFAULT_BRAND_KIT, ...data });
  }, [data]);

  const save = useMutation({
    mutationFn: (next: BrandKit) =>
      api.post<{ ok: true; brandKit: BrandKit }>('/auth/brandkit', next),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.brandKit });
      toast({ tone: 'success', title: t('brandIdentity.savedToast') });
    },
    onError: () => toast({ tone: 'error', title: t('brandIdentity.saveFailedToast') }),
  });

  const patch = useMemo(
    () => (delta: Partial<BrandKit>) => {
      setKit((k) => ({ ...k, ...delta }));
      setDirty(true);
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="page">
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <Palette className="h-7 w-7 text-[var(--color-accent-ink)]" />
            {t('brandIdentity.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('brandIdentity.subtitle')}
          </p>
        </div>
        <Button
          leftIcon={<Save className="h-4 w-4" />}
          loading={save.isPending}
          disabled={uploading || !dirty}
          onClick={() => save.mutate(kit)}
        >
          {t('brandIdentity.saveChanges')}
        </Button>
      </header>

      <Card>
        <CardContent>
          <IdentityTab kit={kit} onChange={patch} onUploadingChange={setUploading} />
        </CardContent>
      </Card>
    </div>
  );
}
